import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Toggle/Switch Component
 *
 * A design-system toggle switch with custom track + thumb styling.
 * Supports reactive forms (CVA), two-way [(checked)] binding, and label projection.
 *
 * Usage:
 * ```html
 * <hc-toggle label="Enable notifications" formControlName="notifications"></hc-toggle>
 * <hc-toggle label="Active" [(checked)]="isActive"></hc-toggle>
 * ```
 */
@Component({
  selector: 'hc-toggle',
  standalone: false,
  templateUrl: './toggle.component.html',
  styleUrl: './toggle.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleComponent),
      multi: true
    }
  ]
})
export class ToggleComponent implements ControlValueAccessor {
  /** Toggle label */
  @Input() label = '';

  /** Whether the toggle is disabled */
  @Input() disabled = false;

  /** Event emitted when toggle state changes */
  @Output() toggled = new EventEmitter<boolean>();

  /** Event emitted for two-way binding support */
  @Output() checkedChange = new EventEmitter<boolean>();

  /** Internal checked state (use with [(checked)] for two-way binding) */
  private _checked = false;

  @Input()
  get checked(): boolean {
    return this._checked;
  }
  set checked(value: boolean) {
    this._checked = value;
  }

  // ControlValueAccessor callbacks
  private onChange: (value: boolean) => void = () => { /* noop */ };
  private onTouched: () => void = () => { /* noop */ };

  /** Toggle the checked state (called from template) */
  toggle(): void {
    if (this.disabled) {
      return;
    }
    this._checked = !this._checked;
    this.onChange(this._checked);
    this.onTouched();
    this.toggled.emit(this._checked);
    this.checkedChange.emit(this._checked);
  }

  // ControlValueAccessor implementation
  writeValue(value: boolean): void {
    this.checked = value ?? false;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
